import React, { Suspense, lazy } from 'react';
import {
  Navigate,
  Outlet,
  RouterProvider,
  createBrowserRouter,
  useLocation,
  useParams,
  useSearchParams,
} from 'react-router-dom';

import { ContentBoardSkeleton } from '@/components/content-board/ContentBoardSkeleton';
import { DiscoverPageSkeleton } from '@/components/discover/DiscoverPageSkeleton';
import AppLayout from '@/components/layout/AppLayout';
import ErrorDisplay from '@/components/layout/ErrorDisplay';
import MainLayout from '@/components/layout/MainLayout';
import { PageContentLoader } from '@/components/layout/PageContentLoader';
import PublicAppLayout from '@/components/layout/PublicAppLayout';
import { SessionMonitorProvider } from '@/components/layout/SessionMonitorProvider';
import { UnifiedErrorBoundary } from '@/components/ui/error-boundary';
import { useAuth } from '@/hooks/use-auth';
import { usePresenceHeartbeat } from '@/hooks/usePresenceHeartbeat';
import {
  ANALYTICS_VIEW_IDS,
  type AnalyticsViewId,
  getAnalyticsOverviewRedirectSearch,
} from '@/lib/analytics/analytics-filter';
import {
  buildGoogleAdsCampaignPath,
  parseGoogleAdsAccountKey,
} from '@/lib/analytics/google-ads-account-identity';
import { PUBLISHING_FEED_TAB_VISIBLE } from '@/lib/feature-flags';
import { ReachEntitledRoute } from '@/lib/reach/ReachEntitledRoute';
import { PublishingLegacyRedirect } from '@/routes/PublishingLegacyRedirect';
import {
  AnalyticsOverview,
  GoogleAdsCampaignDetail,
  LinkedInAdsCampaignDetail,
  MetaAdsCampaignDetail,
} from '@/routes/analytics.lazy';
import { authRedirectPath } from '@/routes/auth-redirect';
import getMoraObjectsLabRoute from '@/routes/moraObjectsLabRoute.dev';
import { lazyWithRetry } from '@/utils/lazyWithRetry';
import { logger } from '@/utils/logger';

const BillingPage = lazyWithRetry(() => import('@/pages/BillingPage'));

// F-082 (UAT 2026-06-12): these pages were imported eagerly, dragging their
// whole subtree (Settings alone pulls the full settings dialog: integrations,
// brand kit, team, billing panels) into the ENTRY's static module graph. The
// built HTML emitted 262 <link rel="modulepreload"> and the browser had to
// resolve all of them before main.tsx could run — observed as a 30–60s
// white screen on cold loads. Routes are the code-split seam; lazy them.
const AcceptInvite = lazyWithRetry(() => import('@/pages/AcceptInvite'));
const DeskPage = lazyWithRetry(() => import('@/pages/desk/DeskPage'));

/**
 * Every `/review/:token` link already in a text message or an inbox keeps
 * working, and lands on the Desk carrying its own token. `replace` so the dead
 * address never enters the reader's history.
 */
function ReviewLinkToDeskRedirect(): JSX.Element {
  const { token } = useParams<{ token: string }>();
  return <Navigate to={`/desk/${token ?? ''}`} replace />;
}
const ReachApprovalsPage = lazyWithRetry(() => import('@/pages/reach/ReachApprovalsPage'));
const ClientDeskPage = lazyWithRetry(() => import('@/pages/calendar/ClientDeskPage'));
const ReachEditorialPage = lazyWithRetry(() => import('@/pages/reach/ReachEditorialPage'));
const Auth = lazyWithRetry(() => import('@/pages/Auth'));
const Onboarding = lazyWithRetry(() => import('@/pages/Onboarding'));
const Settings = lazyWithRetry(() => import('@/pages/Settings'));
const SocialCallback = lazyWithRetry(() => import('@/components/auth/SocialCallback'));
const OAuthAuthorize = lazyWithRetry(() => import('@/pages/OAuthAuthorize'));
const BrainUpload = lazyWithRetry(() => import('@/pages/BrainUpload'));
const ResetPasswordConfirm = lazyWithRetry(() => import('@/components/auth/ResetPasswordConfirm'));
const ResetPasswordRequest = lazyWithRetry(() => import('@/components/auth/ResetPasswordRequest'));

const CreateToHomeRedirect = () => {
  const location = useLocation();
  return (
    <Navigate to={{ pathname: '/', search: location.search }} replace state={location.state} />
  );
};

const AgentsToWorkflowsRedirect = () => {
  const location = useLocation();
  return (
    <Navigate
      to={{ pathname: '/workflows', search: location.search, hash: '#agents' }}
      replace
      state={location.state}
    />
  );
};

// UX PRD §7.1: People is Mora Reach's landing surface, because the product's
// thesis is one person across many appearances (§3.3) rather than one event.
// This pointed at Events while the People phase was unbuilt; it no longer does.
const ReachToPeopleRedirect = () => {
  const location = useLocation();
  return (
    <Navigate
      to={{ pathname: '/reach/people', search: location.search }}
      replace
      state={location.state}
    />
  );
};

// Preserves the query string on the way to `/insights/overview`. A plain
// `<Navigate to="/insights/overview" />` drops any ?view=/&platform= the sidebar
// link (or a deep link) carried, silently resetting the user's tab and filter on
// every click (beta scorecard P1 #6).
//
// This serves the bare `/insights` index AND every legacy `/analytics/*` URL.
// The page was renamed Insights everywhere on 2026-07-24 because the product
// calls it Insights and the URL calling it Analytics was the last place
// contradicting that. `getAnalyticsOverviewRedirectSearch` still maps the old
// per-report aliases (`/business`, `/social`, `/roi`, …) onto the `?view=`
// contract, so those links keep landing on the tab they meant.
const InsightsOverviewRedirect = () => {
  const location = useLocation();
  const search = getAnalyticsOverviewRedirectSearch(location.pathname, location.search);
  return (
    <Navigate
      to={{ pathname: '/insights/overview', search, hash: location.hash }}
      replace
      state={location.state}
    />
  );
};

/**
 * `/insights/audience` (and `/content`, `/competitors`) → `/insights/overview?view=…`.
 *
 * The Insights tabs are `?view=` params, not paths, so the URLs a person would
 * naturally type or share — the ones that match the tab labels they can see —
 * were hitting the 404 dither. Nothing linked to them, which is exactly why it
 * went unnoticed: the only way to find the bug was to guess the obvious URL,
 * which is what a beta tester does first.
 *
 * A redirect rather than a real nested route on purpose. The tab is page STATE
 * (the platform filter and window live beside it in the query string and must
 * survive a tab change), and promoting one of those three to a path would leave
 * the other two as params — three sibling tabs addressed two different ways.
 * `?view=` stays canonical; these are doors into it.
 *
 * The rest of the query string rides along, so `/insights/content?range=90`
 * keeps its window.
 */
const InsightsTabRedirect = ({ view }: { view: AnalyticsViewId }) => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  params.set('view', view);
  return (
    <Navigate
      to={{ pathname: '/insights/overview', search: `?${params.toString()}`, hash: location.hash }}
      replace
      state={location.state}
    />
  );
};

/**
 * `/insights/google-ads` — the Google Ads campaign LIST page — is retired.
 * `32-INSIGHTS-REBUILD-PLAN.md` §1.1: a Google Ads campaign list renders in
 * exactly one place now, `/campaigns` (`PaidCampaignsPage.tsx`).
 *
 * Translates this page's own `?account=` shape
 * (`<connectionId>:<customerId>`, `google-ads-account-identity.ts`) into the
 * one `/campaigns` account filter understands
 * (`google-ads:<accountId>`/`getAvailableAccounts` in
 * `paid-campaign-table.ts`, where `accountId` is the Google `customerId`) —
 * forwarding the raw value would match no row in `/campaigns` and silently
 * render an empty table instead of the account the link meant. Also scopes
 * the destination to the Google Ads channel, since that is what this URL
 * always meant.
 */
export const GoogleAdsCampaignsToPaidRedirect = () => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const accountKey = params.get('account');
  params.delete('account');
  params.set('channel', 'google-ads');
  if (accountKey) {
    const { customerId } = parseGoogleAdsAccountKey(accountKey);
    if (customerId) params.set('account', `google-ads:${customerId}`);
  }
  return (
    <Navigate
      to={{ pathname: '/campaigns', search: `?${params.toString()}`, hash: location.hash }}
      replace
      state={location.state}
    />
  );
};

/**
 * `/campaigns/launches` → the Campaigns tab. The Launch activity ledger page is retired: each
 * campaign's detail page now lists what Mora did to it, and a failed or unfinished attempt is a row
 * in the Campaigns table. The route stays because sent Slack messages link to it.
 */
export const PaidCampaignLaunchesRedirect = () => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  params.set('level', 'campaigns');
  return (
    <Navigate
      to={{ pathname: '/campaigns', search: `?${params.toString()}`, hash: location.hash }}
      replace
      state={location.state}
    />
  );
};

export const PaidCampaignDraftsRedirect = () => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  params.set('level', 'drafts');
  return (
    <Navigate
      to={{ pathname: '/campaigns', search: `?${params.toString()}`, hash: location.hash }}
      replace
      state={location.state}
    />
  );
};

/**
 * `/insights/google-ads/:connectionId/:customerId/campaigns/:campaignId` →
 * the one canonical detail mount,
 * `/campaigns/google-ads/:connectionId/:customerId/:campaignId`. Every path
 * param and the query string (typically `?days=`) travel through unchanged.
 */
export const GoogleAdsCampaignDetailToPaidRedirect = () => {
  const { connectionId, customerId, campaignId } = useParams<{
    connectionId: string;
    customerId: string;
    campaignId: string;
  }>();
  const location = useLocation();
  const pathname = buildGoogleAdsCampaignPath(
    {
      connectionId: connectionId ?? '',
      customerId: customerId ?? '',
      campaignId: campaignId ?? '',
    },
    'campaigns',
  );
  return (
    <Navigate
      to={{ pathname, search: location.search, hash: location.hash }}
      replace
      state={location.state}
    />
  );
};

/**
 * The legacy 2-param shape (`:customerId/campaigns/:campaignId`, no
 * `connectionId` — pre-dates multi-connection accounts) → the matching legacy
 * mount already kept alive under `/campaigns` for the same old links.
 */
export const GoogleAdsCampaignDetailLegacyToPaidRedirect = () => {
  const { customerId, campaignId } = useParams<{ customerId: string; campaignId: string }>();
  const location = useLocation();
  const pathname = ['/campaigns/google-ads', customerId, campaignId]
    .map((segment, index) => (index === 0 ? segment : encodeURIComponent(segment ?? '')))
    .join('/');
  return (
    <Navigate
      to={{ pathname, search: location.search, hash: location.hash }}
      replace
      state={location.state}
    />
  );
};

// `/projects?view=*` — handles legacy view-param shape (documents/media moved
// to top-level routes). Bare /projects renders the projects list.
const ProjectsListWithLegacyViewRedirect = () => {
  const [searchParams] = useSearchParams();
  const view = searchParams.get('view');
  if (view === 'documents') return <Navigate to="/library-assets?kind=documents" replace />;
  if (view === 'media' || view === 'all-media')
    return <Navigate to="/library-assets?kind=media" replace />;
  return <ProjectsPage />;
};

const PageLoader = () => <PageContentLoader />;

// Secondary pages - standard React lazy loading

// Analytics - Import from optimized lazy loading module
const Products = lazyWithRetry(() => import('@/pages/Products'));
const ReachEventsPage = lazyWithRetry(() => import('@/pages/reach/ReachEventsPage'));
/**
 * Keeps every existing `/reach/events/:eventId` link alive after that page was
 * deleted. Lands on the People surface scoped to the event — the work the page
 * actually existed to do — rather than 404ing or dropping the event context.
 */
function ReachEventDetailRedirect(): JSX.Element {
  const { eventId } = useParams<{ eventId: string }>();
  return <Navigate to={`/reach/people?eventId=${eventId ?? ''}`} replace />;
}

const ReachEventBriefPrintPage = lazyWithRetry(
  () => import('@/pages/reach/ReachEventBriefPrintPage'),
);
const ReachPeoplePage = lazyWithRetry(() => import('@/pages/reach/ReachPeoplePage'));
const ReachPersonDetailPage = lazyWithRetry(() => import('@/pages/reach/ReachPersonDetailPage'));
const ReachOutreachPage = lazyWithRetry(() => import('@/pages/reach/ReachOutreachPage'));
const ReachListsPage = lazyWithRetry(() => import('@/pages/reach/ReachListsPage'));
const ReachListDetailPage = lazyWithRetry(() => import('@/pages/reach/ReachListDetailPage'));
const PaidCampaignsPage = lazyWithRetry(() => import('@/pages/campaigns/PaidCampaignsPage'));
const LinkedInCampaignAuthoringPage = lazyWithRetry(
  () => import('@/pages/campaigns/LinkedInCampaignAuthoringPage'),
);
const PaidAdAuthoringPage = lazyWithRetry(() => import('@/pages/campaigns/PaidAdAuthoringPage'));
const PaidCampaignNewAlias = lazyWithRetry(() => import('@/pages/campaigns/PaidCampaignNewAlias'));

// Discover
const DiscoverPage = lazyWithRetry(() => import('@/pages/discover/DiscoverPage'));
const ArticleDetailPage = lazyWithRetry(() => import('@/pages/discover/ArticleDetailPage'));
const DiscoverHealthPage = lazyWithRetry(() => import('@/pages/discover/DiscoverHealthPage'));
const DiscoverAdsPage = lazyWithRetry(() => import('@/pages/discover/DiscoverAdsPage'));

const ContentBoardPage = lazyWithRetry(() => import('@/pages/ContentBoardPage'));
const ComposePostPage = lazyWithRetry(() => import('@/pages/ComposePostPage'));
const TikTokTrendsCuration = lazy(() => import('@/pages/admin/TikTokTrendsCuration'));
const AdminDashboard = lazy(() => import('@/pages/admin/AdminDashboard'));

// Content Plan page removed — the home composer lives at `/`; legacy create
// aliases redirect here.
const CreatePage = lazyWithRetry(() => import('@/pages/create/CreatePage'));

const ProjectsPage = lazyWithRetry(() => import('@/pages/projects/ProjectsPage'));
const AgentsPage = lazyWithRetry(() => import('@/pages/agents/AgentsPage'));
// One canonical Briefs implementation owns list, durable runs, and typed detail.
// The older teaching-shell duplicate under pages/creatives was retired because
// it told users Briefs still lived in Projects after the real list had shipped.
const BriefsPage = lazyWithRetry(() => import('@/pages/briefs/BriefsPage'));
const BriefDetailPage = lazyWithRetry(() => import('@/pages/briefs/BriefDetailPage'));
const AdCopyPage = lazyWithRetry(() => import('@/pages/ad-copy/AdCopyPage'));
const BriefConceptPage = lazyWithRetry(() => import('@/pages/briefs/BriefConceptPage'));
const ConceptWorkbenchPage = lazyWithRetry(() => import('@/pages/creatives/ConceptWorkbenchPage'));
const StudioPage = lazyWithRetry(() => import('@/pages/creatives/StudioPage'));
const AssetsPage = lazyWithRetry(() => import('@/pages/creatives/AssetsPage'));
const PersonasPage = lazyWithRetry(() => import('@/pages/creatives/PersonasPage'));

// Help Center pages
const HelpLayout = lazyWithRetry(() => import('@/layouts/HelpLayout'));
const HelpHome = lazyWithRetry(() => import('@/pages/help/index'));
const HelpArticlePage = lazyWithRetry(() => import('@/pages/help/HelpArticlePage'));
const ContactSupport = lazyWithRetry(() => import('@/pages/ContactSupport'));

const StyleGuideLayout = lazyWithRetry(() =>
  import('@/components/style-guide/StyleGuideLayout').then((m) => ({
    default: m.StyleGuideLayout,
  })),
);
const StyleGuideHome = lazyWithRetry(() =>
  import('@/components/style-guide/StyleGuideHome').then((m) => ({ default: m.StyleGuideHome })),
);
const StyleGuideOnboardingPreview = lazyWithRetry(() =>
  import('@/components/style-guide/OnboardingPreview').then((m) => ({
    default: m.OnboardingPreview,
  })),
);
const StyleGuideEmailPreviews = lazyWithRetry(() =>
  import('@/components/style-guide/EmailPreviews').then((m) => ({
    default: m.EmailPreviews,
  })),
);
const StyleGuideFoundations = lazyWithRetry(() =>
  import('@/components/style-guide/Foundations').then((m) => ({ default: m.Foundations })),
);
const StyleGuideTokens = lazyWithRetry(() =>
  import('@/components/style-guide/Tokens').then((m) => ({ default: m.Tokens })),
);
const StyleGuideTokenColors = lazyWithRetry(() =>
  import('@/components/style-guide/TokenColors').then((m) => ({ default: m.TokenColors })),
);
const StyleGuideTokenTypography = lazyWithRetry(() =>
  import('@/components/style-guide/TokenTypography').then((m) => ({
    default: m.TokenTypography,
  })),
);
const StyleGuideTokenSpacing = lazyWithRetry(() =>
  import('@/components/style-guide/TokenSpacing').then((m) => ({ default: m.TokenSpacing })),
);
const StyleGuideTokenRadius = lazyWithRetry(() =>
  import('@/components/style-guide/TokenRadius').then((m) => ({ default: m.TokenRadius })),
);
const StyleGuideTokenMotion = lazyWithRetry(() =>
  import('@/components/style-guide/TokenMotion').then((m) => ({ default: m.TokenMotion })),
);
const StyleGuideIllustrations = lazyWithRetry(() =>
  import('@/components/style-guide/Illustrations').then((m) => ({ default: m.Illustrations })),
);
const StyleGuideArtLab = lazyWithRetry(() =>
  import('@/components/style-guide/ArtLab').then((m) => ({ default: m.ArtLab })),
);
const StyleGuideBrandKit = lazyWithRetry(() =>
  import('@/components/style-guide/BrandKit').then((m) => ({ default: m.BrandKit })),
);
const StyleGuidePlatformPreviews = lazyWithRetry(() =>
  import('@/components/style-guide/PlatformPreviews').then((m) => ({
    default: m.PlatformPreviews,
  })),
);
const StyleGuideBrandMarketing = lazyWithRetry(() =>
  import('@/components/style-guide/BrandMarketing').then((m) => ({
    default: m.BrandMarketing,
  })),
);
const StyleGuideInspector = lazyWithRetry(() =>
  import('@/components/style-guide/Inspector').then((m) => ({
    default: m.StyleGuideInspector,
  })),
);
const StyleGuideComponentsIndex = lazyWithRetry(() =>
  import('@/components/style-guide/ComponentsIndex').then((m) => ({
    default: m.ComponentsIndex,
  })),
);
const StyleGuideComponentShowcase = lazyWithRetry(() =>
  import('@/components/style-guide/ComponentShowcase').then((m) => ({
    default: m.ComponentShowcase,
  })),
);
const StyleGuidePatterns = lazyWithRetry(() =>
  import('@/components/style-guide/PatternsIndex').then((m) => ({ default: m.PatternsIndex })),
);
const StyleGuidePatternDetail = lazyWithRetry(() =>
  import('@/components/style-guide/patterns/PatternRouter').then((m) => ({
    default: m.PatternRouter,
  })),
);
const StyleGuideCompositions = lazyWithRetry(() =>
  import('@/components/style-guide/compositions/CompositionsIndex').then((m) => ({
    default: m.CompositionsIndex,
  })),
);
const StyleGuideCompChatComposer = lazyWithRetry(() =>
  import('@/components/style-guide/compositions/ChatComposerShowcase').then((m) => ({
    default: m.ChatComposerShowcase,
  })),
);
const StyleGuideCompModelUpgradeDialog = lazyWithRetry(() =>
  import('@/components/style-guide/compositions/ModelUpgradeDialogShowcase').then((m) => ({
    default: m.ModelUpgradeDialogShowcase,
  })),
);
const StyleGuideCompChoiceSurfaces = lazyWithRetry(() =>
  import('@/components/style-guide/compositions/ChoiceSurfacesShowcase').then((m) => ({
    default: m.ChoiceSurfacesShowcase,
  })),
);
const StyleGuideCompQuietList = lazyWithRetry(() =>
  import('@/components/style-guide/compositions/QuietListShowcase').then((m) => ({
    default: m.QuietListShowcase,
  })),
);
const StyleGuideCompDataTable = lazyWithRetry(() =>
  import('@/components/style-guide/compositions/DataTableShowcase').then((m) => ({
    default: m.DataTableShowcase,
  })),
);
const StyleGuideCompCreateRightRail = lazyWithRetry(() =>
  import('@/components/style-guide/compositions/CreateRightRailShowcase').then((m) => ({
    default: m.CreateRightRailShowcase,
  })),
);
const StyleGuideCompMediaStudio = lazyWithRetry(() =>
  import('@/components/style-guide/compositions/MediaStudioShowcase').then((m) => ({
    default: m.MediaStudioShowcase,
  })),
);
const StyleGuideCompDocumentSplit = lazyWithRetry(() =>
  import('@/components/style-guide/compositions/DocumentSplitViewShowcase').then((m) => ({
    default: m.DocumentSplitViewShowcase,
  })),
);
const StyleGuideCompDiscoverFeed = lazyWithRetry(() =>
  import('@/components/style-guide/compositions/DiscoverFeedShowcase').then((m) => ({
    default: m.DiscoverFeedShowcase,
  })),
);
const StyleGuideCompAITemporary = lazyWithRetry(() =>
  import('@/components/style-guide/compositions/AITemporaryShowcase').then((m) => ({
    default: m.AITemporaryShowcase,
  })),
);
const StyleGuideCompPills = lazyWithRetry(() =>
  import('@/components/style-guide/compositions/PillsShowcase').then((m) => ({
    default: m.PillsShowcase,
  })),
);
const StyleGuideCompPopovers = lazyWithRetry(() =>
  import('@/components/style-guide/compositions/PopoverShowcase').then((m) => ({
    default: m.PopoverShowcase,
  })),
);
const StyleGuideCompProjectContextRail = lazyWithRetry(() =>
  import('@/components/style-guide/compositions/ProjectContextRailShowcase').then((m) => ({
    default: m.ProjectContextRailShowcase,
  })),
);
const StyleGuideCompArtifactCards = lazyWithRetry(() =>
  import('@/components/style-guide/compositions/ArtifactCardsShowcase').then((m) => ({
    default: m.ArtifactCardsShowcase,
  })),
);
const StyleGuideCompIntegrationsRedesign = lazyWithRetry(() =>
  import('@/components/style-guide/compositions/IntegrationsRedesignShowcase').then((m) => ({
    default: m.IntegrationsRedesignShowcase,
  })),
);
const StyleGuideCompOAuthConnectionLab = lazyWithRetry(() =>
  import('@/components/style-guide/compositions/OAuthConnectionsLabShowcase').then((m) => ({
    default: m.OAuthConnectionsLabShowcase,
  })),
);
const StyleGuideCompPostInspector = lazyWithRetry(() =>
  import('@/components/style-guide/compositions/PostInspectorShowcase').then((m) => ({
    default: m.PostInspectorShowcase,
  })),
);
const StyleGuideCompUnifiedMediaStudio = lazyWithRetry(() =>
  import('@/components/style-guide/compositions/UnifiedMediaStudioShowcase').then((m) => ({
    default: m.UnifiedMediaStudioShowcase,
  })),
);
const StyleGuideCompTeamSettings = lazyWithRetry(() =>
  import('@/components/style-guide/compositions/TeamSettingsShowcase').then((m) => ({
    default: m.TeamSettingsShowcase,
  })),
);
const StyleGuideCompUserMenu = lazyWithRetry(() =>
  import('@/components/style-guide/compositions/UserMenuShowcase').then((m) => ({
    default: m.UserMenuShowcase,
  })),
);
const StyleGuideCompWorkflowLibrary = lazyWithRetry(() =>
  import('@/components/style-guide/compositions/WorkflowLibraryShowcase').then((m) => ({
    default: m.WorkflowLibraryShowcase,
  })),
);
const StyleGuideCompEngagementInbox = lazyWithRetry(() =>
  import('@/components/style-guide/compositions/EngagementInboxShowcase').then((m) => ({
    default: m.EngagementInboxShowcase,
  })),
);
const StyleGuideCompBusinessHealthScore = lazyWithRetry(() =>
  import('@/components/style-guide/compositions/BusinessHealthScoreLabShowcase').then((m) => ({
    default: m.BusinessHealthScoreLabShowcase,
  })),
);
const StyleGuideCompWorkbenchPrimitives = lazyWithRetry(() =>
  import('@/components/style-guide/compositions/WorkbenchPrimitivesShowcase').then((m) => ({
    default: m.WorkbenchPrimitivesShowcase,
  })),
);
const StyleGuideCompToolViewGallery = lazyWithRetry(() =>
  import('@/components/style-guide/compositions/ToolViewGalleryShowcase').then((m) => ({
    default: m.ToolViewGalleryShowcase,
  })),
);
const StyleGuideCompChatSurfaces = lazyWithRetry(() =>
  import('@/components/style-guide/compositions/ChatSurfacesShowcase').then((m) => ({
    default: m.ChatSurfacesShowcase,
  })),
);
const StyleGuideVoice = lazyWithRetry(() =>
  import('@/components/style-guide/Voice').then((m) => ({ default: m.Voice })),
);
const StyleGuideA11y = lazyWithRetry(() =>
  import('@/components/style-guide/A11yPage').then((m) => ({ default: m.A11yPage })),
);
const NotFound = lazyWithRetry(() => import('@/pages/NotFound'));

// Calendar pages
const CalendarLayout = lazyWithRetry(() => import('@/components/calendar/CalendarLayout'));
const ScheduledPage = lazyWithRetry(() => import('@/pages/calendar/ScheduledPage'));
const PostsPage = lazyWithRetry(() => import('@/pages/calendar/PostsPage'));
const FeedPage = lazyWithRetry(() => import('@/pages/calendar/FeedPage'));
const DeliveryPage = lazyWithRetry(() => import('@/pages/calendar/DeliveryPage'));

// Enhanced protected route with session validation
const ProtectedRoute = ({ children }: { children: React.ReactNode }): React.ReactNode => {
  const { user, loading, session } = useAuth();
  const location = useLocation();

  // Presence heartbeat for every authenticated route (powers the admin
  // "currently online" view). Self-throttled + visibility-aware; no-ops until
  // the user is authed.
  usePresenceHeartbeat(!!user && !!session);

  // Always wait for auth context to fully resolve
  if (loading) {
    return <PageLoader />;
  }

  // Check for both user and valid session
  if (!user || !session) {
    return <Navigate to={authRedirectPath(location)} replace />;
  }

  // Additional session expiry check
  if (session.expires_at && parseInt(session.expires_at.toString()) * 1000 <= Date.now()) {
    logger.warn('Session expired, redirecting to auth');
    return <Navigate to={authRedirectPath(location)} replace />;
  }

  return <>{children}</>;
};

function authRouteReturnTo(): string {
  if (typeof window === 'undefined') return '/';
  const raw = new URLSearchParams(window.location.search).get('return_to');
  if (!raw) return '/';
  try {
    const parsed = new URL(raw, window.location.origin);
    return parsed.origin === window.location.origin && !parsed.pathname.includes('\\')
      ? parsed.pathname + parsed.search + parsed.hash
      : '/';
  } catch {
    return '/';
  }
}

// Auth route: an established session still passes through the durable
// onboarding gate. It decides completion from the profile row instead of
// letting a stale auth-metadata flag drop a new user at Home.
const AuthRoute = ({ children }: { children: React.ReactNode }): React.ReactNode => {
  const { user, loading } = useAuth();
  const isEmailConfirmationCallback =
    typeof window !== 'undefined' &&
    new URLSearchParams(window.location.search).get('confirmation') === '1';

  if (loading) {
    return <PageLoader />;
  }

  if (user && !isEmailConfirmationCallback) {
    return <Navigate to="/onboarding" replace state={{ postAuthReturnTo: authRouteReturnTo() }} />;
  }

  return <>{children}</>;
};

// MainLayout is now imported at the top to avoid nested Suspense issues

// Protected layout component with proper Suspense and error boundaries
const ProtectedLayout = () => {
  return (
    <UnifiedErrorBoundary level="app">
      <ProtectedRoute>
        <SessionMonitorProvider>
          <UnifiedErrorBoundary level="app">
            <AppLayout>
              <MainLayout>
                <Suspense fallback={<PageContentLoader />}>
                  <Outlet />
                </Suspense>
              </MainLayout>
            </AppLayout>
          </UnifiedErrorBoundary>
        </SessionMonitorProvider>
      </ProtectedRoute>
    </UnifiedErrorBoundary>
  );
};

// Analytics layout component - providers moved to app level to prevent re-mounting
const AnalyticsLayout = () => {
  return <Outlet />;
};

// Design benches. DEV-ONLY — never mounted in a production build.
//
// Wave 3 (2026-07-31) cut this block from 32 routes to 3. The bar for keeping
// a bench: a CURRENT source-of-truth doc names it as the qualitative ruler for
// live work. All three below are named in `src/components/content-board/README.md`
// and `docs/planning/CONTENT-BOARD-HANDOFF-V3.md` as the acceptance surfaces the
// Content Board is judged against (jsdom has no CSS engine, so these are the only
// place contrast/layout regressions are visible). The other 29 were one-off design
// bake-offs for work that has since shipped — `/test/campaign-flow-canvas` is
// superseded by the live canvas in `/create`'s right drawer, `/test/content-board`
// by `/board`, `/test/home-create-unified` by `/`, and so on.
//
// If you add a bench here, add it here and nowhere else: a route object outside
// this function is a route that ships to strangers. `getDevTestRoutes.test.ts`
// enforces that.
const getDevTestRoutes = () => {
  const BoardEvalHarness = lazy(() => import('@/pages/test/BoardEvalHarness'));
  const CardLab = lazy(() => import('@/pages/test/CardLab'));
  const IdeaSurfaceLab = lazy(() => import('@/pages/test/IdeaSurfaceLab'));
  const JobLab = lazy(() => import('@/pages/test/JobLab'));
  const ReachEventCardLab = lazy(() => import('@/pages/test/ReachEventCardLab'));

  return [
    {
      path: '/test/job-lab',
      element: (
        <Suspense fallback={<PageLoader />}>
          <JobLab />
        </Suspense>
      ),
    },
    {
      path: '/test/board-eval',
      element: (
        <Suspense fallback={<PageLoader />}>
          <BoardEvalHarness />
        </Suspense>
      ),
    },
    {
      path: '/test/card-lab',
      element: (
        <Suspense fallback={<PageLoader />}>
          <CardLab />
        </Suspense>
      ),
    },
    {
      path: '/test/idea-surfaces',
      element: (
        <Suspense fallback={<PageLoader />}>
          <IdeaSurfaceLab />
        </Suspense>
      ),
    },
    {
      path: '/test/reach-event-card',
      element: (
        <Suspense fallback={<PageLoader />}>
          <ReachEventCardLab />
        </Suspense>
      ),
    },
    getMoraObjectsLabRoute(),
  ];
};

function createRouter(): ReturnType<typeof createBrowserRouter> {
  const router = createBrowserRouter(
    [
      {
        path: '/discover',
        element: <PublicAppLayout />,
        errorElement: <ErrorDisplay />,
        children: [
          {
            index: true,
            element: (
              <UnifiedErrorBoundary level="page" resetKeys={['discover']}>
                <Suspense fallback={<DiscoverPageSkeleton />}>
                  <DiscoverPage />
                </Suspense>
              </UnifiedErrorBoundary>
            ),
          },
          {
            /* Retired 2026-10-05: "Keep for later" and its Library were a second
               save beside "Pin to Content ideas". Old links land on the board. */
            path: 'library',
            element: <Navigate to="/board" replace />,
          },
          {
            path: 'health',
            element: (
              <UnifiedErrorBoundary level="page" resetKeys={['discover-health']}>
                <DiscoverHealthPage />
              </UnifiedErrorBoundary>
            ),
          },
          {
            path: 'ads',
            element: (
              <UnifiedErrorBoundary level="page" resetKeys={['discover-ads']}>
                <DiscoverAdsPage />
              </UnifiedErrorBoundary>
            ),
          },
          {
            path: ':slug',
            element: (
              <UnifiedErrorBoundary level="page" resetKeys={['discover-article']}>
                <ArticleDetailPage />
              </UnifiedErrorBoundary>
            ),
          },
        ],
      },
      {
        path: '/',
        element: <ProtectedLayout />,
        errorElement: <ErrorDisplay />,
        children: [
          {
            index: true,
            element: <CreatePage />,
          },
          {
            path: 'settings/*',
            element: (
              <UnifiedErrorBoundary level="page" resetKeys={['settings']}>
                <Settings />
              </UnifiedErrorBoundary>
            ),
          },
          {
            // Legacy manual-post trampoline. Plain /compose redirects home;
            // deep links mint a draft and open the canonical PostBody editor.
            path: 'compose',
            element: (
              <Suspense fallback={<PageLoader />}>
                <ComposePostPage />
              </Suspense>
            ),
          },
          {
            // Content Board — nested under Discover in the sidebar collapsible
            // (DiscoverNavGroup). VITE_FEATURE_CONTENT_BOARD=true in prod.
            path: 'board',
            element: (
              <Suspense fallback={<ContentBoardSkeleton />}>
                <ContentBoardPage />
              </Suspense>
            ),
          },
          {
            // Backward-compatible dashboard alias retained for legacy links.
            path: 'dashboard',
            element: <Navigate to="/" replace />,
          },
          {
            path: 'insights',
            element: (
              <UnifiedErrorBoundary level="page" resetKeys={['insights']}>
                <AnalyticsLayout />
              </UnifiedErrorBoundary>
            ),
            children: [
              {
                index: true,
                element: <InsightsOverviewRedirect />,
              },
              {
                path: 'overview',
                element: <AnalyticsOverview />,
              },
              // Generated from `ANALYTICS_VIEW_IDS` rather than hand-listed: a
              // future tab gets its shareable URL by existing, instead of by
              // someone remembering to add a route here.
              ...ANALYTICS_VIEW_IDS.filter((view) => view !== 'overview').map((view) => ({
                path: view,
                element: <InsightsTabRedirect view={view} />,
              })),
              {
                path: 'google-ads',
                element: <GoogleAdsCampaignsToPaidRedirect />,
              },
              {
                path: 'google-ads/:connectionId/:customerId/campaigns/:campaignId',
                element: <GoogleAdsCampaignDetailToPaidRedirect />,
              },
              {
                path: 'google-ads/:customerId/campaigns/:campaignId',
                element: <GoogleAdsCampaignDetailLegacyToPaidRedirect />,
              },
            ],
          },
          {
            // The URL says what the page says. This section shipped at
            // `/publishing/*` while its header, its tab strip and its own
            // <title> all say "Publishing" — founder, 2026-08-23: _"we need to
            // update that URL because right now it says calendar but the thing
            // says Publishing... it's a little bit confusing with the
            // breadcrumbs."_ `/publishing/*` stays alive as a permanent redirect
            // below: it is in saved links, in agent deep-links, in report
            // emails, and in `publish-failures.ts` remedies.
            path: 'publishing',
            element: (
              <UnifiedErrorBoundary level="page" resetKeys={['publishing']}>
                <CalendarLayout />
              </UnifiedErrorBoundary>
            ),
            children: [
              {
                index: true,
                element: <Navigate to="/publishing/scheduled" replace />,
              },
              {
                path: 'scheduled',
                element: <ScheduledPage />,
              },
              {
                path: 'posts',
                element: <PostsPage />,
              },
              {
                // Suppressed, not removed — `PUBLISHING_FEED_TAB_VISIBLE`
                // (feature-flags.ts) is the founder's 2026-08-23 temporary
                // hide. The route still RESOLVES so any saved link or
                // deep-link from an agent lands somewhere sensible instead of
                // a 404, and `FeedPage` stays referenced here so the whole
                // feed-view lane remains reachable in the import graph and
                // survives dead-code sweeps. Flip the constant to restore.
                path: 'feed',
                element: PUBLISHING_FEED_TAB_VISIBLE ? (
                  <FeedPage />
                ) : (
                  <Navigate to="/publishing/scheduled" replace />
                ),
              },
              {
                path: 'delivery',
                element: <DeliveryPage />,
              },
            ],
          },
          {
            // Permanent home for every `/publishing/*` link ever issued. A
            // wildcard, not a list, so a path added under `/publishing` later
            // keeps working from the old prefix without anyone remembering to
            // mirror it here.
            path: 'calendar/*',
            element: <PublishingLegacyRedirect />,
          },
          {
            path: 'campaigns',
            element: (
              <UnifiedErrorBoundary level="page" resetKeys={['campaigns']}>
                <PaidCampaignsPage />
              </UnifiedErrorBoundary>
            ),
          },
          {
            path: 'campaigns/launches',
            element: <PaidCampaignLaunchesRedirect />,
          },
          {
            path: 'campaigns/drafts',
            element: <PaidCampaignDraftsRedirect />,
          },
          {
            path: 'campaigns/google/new',
            element: (
              <UnifiedErrorBoundary level="page" resetKeys={['campaigns', 'google', 'new']}>
                <PaidCampaignNewAlias provider="google-ads" />
              </UnifiedErrorBoundary>
            ),
          },
          {
            path: 'campaigns/meta/new',
            element: (
              <UnifiedErrorBoundary level="page" resetKeys={['campaigns', 'meta', 'new']}>
                <PaidCampaignNewAlias provider="meta-ads" />
              </UnifiedErrorBoundary>
            ),
          },
          {
            path: 'campaigns/linkedin/new',
            element: (
              <UnifiedErrorBoundary level="page" resetKeys={['campaigns', 'linkedin', 'new']}>
                <PaidCampaignNewAlias
                  provider="linkedin-ads"
                  legacy={<LinkedInCampaignAuthoringPage />}
                />
              </UnifiedErrorBoundary>
            ),
          },
          {
            path: 'campaigns/ads/new/:draftId?',
            element: (
              <UnifiedErrorBoundary level="page" resetKeys={['campaigns', 'ads', 'new']}>
                <PaidAdAuthoringPage />
              </UnifiedErrorBoundary>
            ),
          },
          {
            path: 'campaigns/google-ads/:connectionId/:customerId/:campaignId',
            element: <GoogleAdsCampaignDetail />,
          },
          {
            path: 'campaigns/google-ads/:customerId/:campaignId',
            element: <GoogleAdsCampaignDetail />,
          },
          {
            path: 'campaigns/meta-ads/:connectionId/:selectedResourceId/:adAccountId/:campaignId',
            element: <MetaAdsCampaignDetail />,
          },
          {
            path: 'campaigns/linkedin-ads/:connectionId/:adAccountId/:campaignGroupId',
            element: <LinkedInAdsCampaignDetail />,
          },
          {
            path: 'reach',
            element: <ReachToPeopleRedirect />,
          },
          {
            // Hiding the sidebar row is not a gate — the URL still works. Every
            // Reach route renders through ReachEntitledRoute. It is not the
            // security boundary (RLS is); it stops someone wandering onto a
            // surface their workspace was never given.
            path: 'reach/people',
            element: (
              <UnifiedErrorBoundary level="page" resetKeys={['reach', 'people']}>
                <ReachEntitledRoute>
                  <ReachPeoplePage />
                </ReachEntitledRoute>
              </UnifiedErrorBoundary>
            ),
          },
          {
            path: 'reach/people/:personId',
            element: (
              <UnifiedErrorBoundary level="page" resetKeys={['reach', 'people', 'detail']}>
                <ReachEntitledRoute>
                  <ReachPersonDetailPage />
                </ReachEntitledRoute>
              </UnifiedErrorBoundary>
            ),
          },
          {
            path: 'reach/outreach',
            element: (
              <UnifiedErrorBoundary level="page" resetKeys={['reach', 'outreach']}>
                <ReachEntitledRoute>
                  <ReachOutreachPage />
                </ReachEntitledRoute>
              </UnifiedErrorBoundary>
            ),
          },
          {
            path: 'reach/lists',
            element: (
              <UnifiedErrorBoundary level="page" resetKeys={['reach', 'lists']}>
                <ReachEntitledRoute>
                  <ReachListsPage />
                </ReachEntitledRoute>
              </UnifiedErrorBoundary>
            ),
          },
          {
            /*
             * Deliberately OUTSIDE `<ReachEntitledRoute>`. The client desk is
             * not a consulting module — it is how any workspace gets a decision
             * from the person it makes the work for, and gating it behind a
             * two-id allowlist is what made it unreachable for 32 of 34
             * workspaces. See the comment on CLIENT_DESK_TAB.
             */
            path: 'publishing/client-desk',
            element: (
              <UnifiedErrorBoundary level="page" resetKeys={['publishing', 'client-desk']}>
                <ClientDeskPage />
              </UnifiedErrorBoundary>
            ),
          },
          {
            path: 'reach/approvals',
            element: (
              <UnifiedErrorBoundary level="page" resetKeys={['reach', 'approvals']}>
                <ReachEntitledRoute>
                  <ReachApprovalsPage />
                </ReachEntitledRoute>
              </UnifiedErrorBoundary>
            ),
          },
          {
            path: 'reach/editorial',
            element: (
              <UnifiedErrorBoundary level="page" resetKeys={['reach', 'editorial']}>
                <ReachEntitledRoute>
                  <ReachEditorialPage />
                </ReachEntitledRoute>
              </UnifiedErrorBoundary>
            ),
          },
          {
            path: 'reach/lists/:listId',
            element: (
              <UnifiedErrorBoundary level="page" resetKeys={['reach', 'lists', 'detail']}>
                <ReachEntitledRoute>
                  <ReachListDetailPage />
                </ReachEntitledRoute>
              </UnifiedErrorBoundary>
            ),
          },
          {
            path: 'reach/events',
            element: (
              <UnifiedErrorBoundary level="page" resetKeys={['reach', 'events']}>
                <ReachEntitledRoute>
                  <ReachEventsPage />
                </ReachEntitledRoute>
              </UnifiedErrorBoundary>
            ),
          },
          {
            /*
             * REDIRECT, not a page (2026-08-25). `/reach/events/:eventId` used
             * to be a five-tab workspace whose People and Outreach tabs mounted
             * the same components `/reach/people` and `/reach/outreach` render,
             * and whose Briefing tab duplicated the standalone print route —
             * the product built twice, one route deeper.
             *
             * An event's own facts now open in the right dock from the event
             * card. Its PEOPLE are the work, so an old link lands on the People
             * surface scoped to that event rather than 404ing.
             */
            path: 'reach/events/:eventId',
            element: <ReachEventDetailRedirect />,
          },
          {
            path: 'products',
            element: <Products />,
          },
          {
            path: 'projects/:projectId',
            element: (
              <UnifiedErrorBoundary level="page">
                <ProjectsPage />
              </UnifiedErrorBoundary>
            ),
          },
          {
            // 🔴 DEPRECATED — founder, 2026-08-23. Projects is NOT in use. It was
            // already retired as a sidebar nav noun; the route stays only because
            // the founder may bring the surface back.
            //
            // Do not treat it as a live page: no design passes, no QA effort, and
            // do not cite it when answering "what pages does this app have".
            // Recorded in `src/pages/README.md` § Deprecated surfaces, which is
            // the answer to that question.
            path: 'projects',
            element: (
              <UnifiedErrorBoundary level="page">
                <ProjectsListWithLegacyViewRedirect />
              </UnifiedErrorBoundary>
            ),
          },
          {
            // `/agents` was the first gallery URL. Workflows is now the product
            // destination; keep old bookmarks useful without maintaining two
            // live URLs for one catalog.
            path: 'agents',
            element: <AgentsToWorkflowsRedirect />,
          },
          {
            // Briefs — the plan for one batch of work. A brief IS a `projects`
            // row (MORA-IA-SIMPLIFICATION-2026-08-07 §4 ruling).
            //
            // `/briefs` is the canonical home (flattened 2026-08-15 from
            // `/creatives/briefs` — the sidebar stopped nesting anything under
            // a "Creatives" parent on 2026-08-12, so the URL namespace no
            // longer should either; see `CreativesLegacyRedirects.tsx`).
            path: 'briefs',
            element: (
              <UnifiedErrorBoundary level="page">
                <BriefsPage />
              </UnifiedErrorBoundary>
            ),
          },
          {
            path: 'briefs/:briefId',
            element: (
              <UnifiedErrorBoundary level="page">
                <BriefDetailPage />
              </UnifiedErrorBoundary>
            ),
          },
          {
            // The ad-copy library — every `ad_units` row for the account,
            // Brief-attached or not. Not a sub-row of Briefs: most rows in
            // production have no `brief_id` at all, so nesting this under
            // `/briefs/*` would misstate what it lists. Reached via ⌘K
            // (`destinations.ts`) and a labelled header link on Briefs,
            // mirroring Studio's "View assets" link — no sidebar row, per the
            // founder's standing "nothing nests under Creatives, and ad copy
            // does not get its own sidebar row" ruling (`app-sidebar.tsx`).
            path: 'ad-copy',
            element: (
              <UnifiedErrorBoundary level="page">
                <AdCopyPage />
              </UnifiedErrorBoundary>
            ),
          },
          {
            path: 'briefs/:briefId/concepts/:projectTaskId',
            element: (
              <UnifiedErrorBoundary level="page">
                <BriefConceptPage />
              </UnifiedErrorBoundary>
            ),
          },
          {
            // The Concept Workbench. A SIBLING of the form page above, not a
            // replacement (ruling O7): the form stays the canonical place to
            // write a brief, this is where the artifact gets designed. Both
            // share the concept row, the RPC and its version lock.
            path: 'briefs/:briefId/concepts/:projectTaskId/design',
            element: (
              <UnifiedErrorBoundary level="page">
                <ConceptWorkbenchPage />
              </UnifiedErrorBoundary>
            ),
          },
          {
            // Generation surface. Owns Avatars (a saved likeness — the
            // primitive shipped as "Faces" until 2026-08-07); formats and
            // Looks join it in Stage 5.
            path: 'studio',
            element: (
              <UnifiedErrorBoundary level="page">
                <StudioPage />
              </UnifiedErrorBoundary>
            ),
          },
          {
            path: 'library-assets',
            element: (
              <UnifiedErrorBoundary level="page">
                <AssetsPage />
              </UnifiedErrorBoundary>
            ),
          },
          {
            // Contextual library reached from Brief attachment; no sidebar row.
            path: 'personas',
            element: (
              <UnifiedErrorBoundary level="page">
                <PersonasPage />
              </UnifiedErrorBoundary>
            ),
          },
          {
            path: 'workflows',
            element: (
              <UnifiedErrorBoundary level="page">
                <AgentsPage />
              </UnifiedErrorBoundary>
            ),
          },
          {
            path: 'create',
            element: <CreateToHomeRedirect />,
          },
        ],
      },
      // Public Help Center routes - restructured to avoid layout nesting issues
      {
        path: '/help',
        element: (
          <Suspense fallback={<PageLoader />}>
            <HelpLayout />
          </Suspense>
        ),
        errorElement: <ErrorDisplay />,
        children: [
          {
            index: true,
            element: <HelpHome />,
          },
          {
            path: 'docs',
            element: <HelpHome />,
          },
          {
            path: 'docs/:docId',
            element: <HelpArticlePage />,
          },
          {
            path: 'contact',
            element: <ContactSupport />,
          },
        ],
      },
      {
        path: '/billing',
        element: (
          <ProtectedRoute>
            <Suspense fallback={<PageLoader />}>
              <BillingPage />
            </Suspense>
          </ProtectedRoute>
        ),
        errorElement: <ErrorDisplay />,
      },
      {
        // Mark's printable classify sheet (UX PRD §20.5). Deliberately NOT under
        // ProtectedLayout: the requirement is "no app sidebar/header", because the
        // standard app shell must not print. Authenticated, but unlayouted — the
        // same shape /billing uses.
        path: '/reach/events/:eventId/brief',
        element: (
          <ProtectedRoute>
            <Suspense fallback={<PageLoader />}>
              <ReachEntitledRoute>
                <ReachEventBriefPrintPage />
              </ReachEntitledRoute>
            </Suspense>
          </ProtectedRoute>
        ),
        errorElement: <ErrorDisplay />,
      },
      {
        path: '/onboarding',
        element: (
          <ProtectedRoute>
            <Suspense fallback={<PageLoader />}>
              <Onboarding />
            </Suspense>
          </ProtectedRoute>
        ),
        errorElement: <ErrorDisplay />,
      },
      {
        path: '/auth',
        element: (
          <AuthRoute>
            <Suspense fallback={<PageLoader />}>
              <Auth />
            </Suspense>
          </AuthRoute>
        ),
        errorElement: <ErrorDisplay />,
      },
      {
        path: '/auth/callback',
        element: (
          <Suspense fallback={<PageLoader />}>
            <SocialCallback />
          </Suspense>
        ),
      },
      {
        // OAuth 2.1 `authorization_endpoint` for the MCP resource server
        // (docs/planning/MCP-SERVER-2026-08-02.md §12). Deliberately NOT
        // wrapped in <ProtectedRoute> or <AuthRoute> — the page handles both
        // the signed-in and signed-out cases itself, since an external OAuth
        // client can open this URL for a user who isn't signed in yet.
        path: '/oauth/authorize',
        element: (
          <Suspense fallback={<PageLoader />}>
            <OAuthAuthorize />
          </Suspense>
        ),
        errorElement: <ErrorDisplay />,
      },
      {
        path: '/reset-password',
        element: (
          <AuthRoute>
            <Suspense fallback={<PageLoader />}>
              <ResetPasswordRequest />
            </Suspense>
          </AuthRoute>
        ),
      },
      {
        path: '/reset-password/confirm',
        element: (
          <Suspense fallback={<PageLoader />}>
            <ResetPasswordConfirm />
          </Suspense>
        ),
      },
      {
        path: '/accept-invite',
        element: (
          <Suspense fallback={<PageLoader />}>
            <AcceptInvite />
          </Suspense>
        ),
      },
      /*
       * The client's link. Deliberately OUTSIDE ProtectedLayout AND outside
       * ProtectedRoute: the whole point of this surface is that a busy client
       * decides without an account, on a phone, from a text message.
       *
       * It is not a hole in the auth model. Every read and write goes through
       * /api/desk/* and /api/review/*, which resolve the URL token to exactly
       * one reviewer with the service-role key; no approval_* table is granted
       * to anon, so the token is a key to one desk rather than a bearer
       * credential for a table.
       *
       * `/review/:token` REDIRECTS rather than 404s, and keeps the token. Links
       * are already out in the world in text messages and email, and breaking
       * one would strand the exact person this surface exists to serve. The
       * page behind it is gone; only the address survives.
       */
      {
        path: '/review/:token',
        element: <ReviewLinkToDeskRedirect />,
      },

      /*
       * The Content Desk — the same token, the same no-login guarantee, and the
       * surface that supersedes /review/:token above. The review page stays
       * mounted only until every issued link has been re-pointed here; it is
       * scheduled for deletion, not kept as an alternative.
       */
      {
        path: '/desk/:token',
        element: (
          <Suspense fallback={<PageLoader />}>
            <DeskPage />
          </Suspense>
        ),
      },

      /*
       * One-time upload link minted by the MCP tool `request_upload_link`.
       * Public like /desk/:token — the token is the credential, and the
       * server takes owner + workspace from the link row, never the page
       * (server/api/knowledge/upload-link/).
       */
      {
        path: '/brain/upload/:token',
        element: (
          <Suspense fallback={<PageLoader />}>
            <BrainUpload />
          </Suspense>
        ),
      },

      {
        path: '*',
        element: (
          <Suspense fallback={<PageLoader />}>
            <NotFound />
          </Suspense>
        ),
      },
      // /style-guide ships in production on purpose — DS-9 governance promotion
      // 2026-04-25, docs/design-system/GOVERNANCE.md §9: contractors and reviewers
      // verify changes against the canonical UI without spinning up a dev server.
      // That decision stands.
      //
      // What did NOT stand: §9 also states "Auth still required (the rest of the app
      // gates on auth)". That was never true. This is a TOP-LEVEL route — it is not a
      // child of `ProtectedLayout` and it never rendered `ProtectedRoute`, so the app
      // shell it was assumed to inherit was never in the tree. Verified 2026-07-31
      // against a real production build (`vite preview`) with no session: every product
      // route redirected to /auth and `/style-guide` rendered the full design system —
      // component inventory, brand kit, email templates, the onboarding walkthrough, and
      // every internal composition (engagement inbox, team settings, post inspector) —
      // to an anonymous visitor at a guessable URL on the production domain.
      //
      // The fix is the one that makes §9 true rather than reversing it: keep the route in
      // production, wrap it in `ProtectedRoute` like `/billing` and `/admin` already are.
      // Reviewers still get the URL; strangers get /auth.
      {
        path: '/style-guide',
        element: (
          <ProtectedRoute>
            <Suspense fallback={<PageLoader />}>
              <StyleGuideLayout />
            </Suspense>
          </ProtectedRoute>
        ),
        children: [
          { index: true, element: <StyleGuideHome /> },
          { path: 'onboarding', element: <StyleGuideOnboardingPreview /> },
          { path: 'emails', element: <StyleGuideEmailPreviews /> },
          { path: 'foundations', element: <StyleGuideFoundations /> },
          { path: 'tokens', element: <StyleGuideTokens /> },
          { path: 'tokens/colors', element: <StyleGuideTokenColors /> },
          { path: 'tokens/typography', element: <StyleGuideTokenTypography /> },
          { path: 'tokens/spacing', element: <StyleGuideTokenSpacing /> },
          { path: 'tokens/radius', element: <StyleGuideTokenRadius /> },
          { path: 'tokens/motion', element: <StyleGuideTokenMotion /> },
          { path: 'illustrations', element: <StyleGuideIllustrations /> },
          { path: 'art-lab', element: <StyleGuideArtLab /> },
          { path: 'brand-kit', element: <StyleGuideBrandKit /> },
          { path: 'brand-marketing', element: <StyleGuideBrandMarketing /> },
          { path: 'platform-previews', element: <StyleGuidePlatformPreviews /> },
          { path: 'components/inspector', element: <StyleGuideInspector /> },
          { path: 'components', element: <StyleGuideComponentsIndex /> },
          { path: 'components/:name', element: <StyleGuideComponentShowcase /> },
          { path: 'patterns', element: <StyleGuidePatterns /> },
          { path: 'patterns/:name', element: <StyleGuidePatternDetail /> },
          { path: 'compositions', element: <StyleGuideCompositions /> },
          { path: 'compositions/chat-composer', element: <StyleGuideCompChatComposer /> },
          {
            path: 'compositions/model-upgrade-dialog',
            element: <StyleGuideCompModelUpgradeDialog />,
          },
          { path: 'compositions/choice-surfaces', element: <StyleGuideCompChoiceSurfaces /> },
          { path: 'compositions/quiet-list', element: <StyleGuideCompQuietList /> },
          { path: 'compositions/data-table', element: <StyleGuideCompDataTable /> },
          {
            path: 'compositions/create-right-rail',
            element: <StyleGuideCompCreateRightRail />,
          },
          { path: 'compositions/media-studio', element: <StyleGuideCompMediaStudio /> },
          {
            path: 'compositions/document-split-view',
            element: <StyleGuideCompDocumentSplit />,
          },
          { path: 'compositions/discover-feed', element: <StyleGuideCompDiscoverFeed /> },
          { path: 'compositions/ai-temporary', element: <StyleGuideCompAITemporary /> },
          { path: 'compositions/pills', element: <StyleGuideCompPills /> },
          { path: 'compositions/popovers', element: <StyleGuideCompPopovers /> },
          {
            path: 'compositions/project-context-rail',
            element: <StyleGuideCompProjectContextRail />,
          },
          { path: 'compositions/artifact-cards', element: <StyleGuideCompArtifactCards /> },
          {
            path: 'compositions/integrations-redesign',
            element: <StyleGuideCompIntegrationsRedesign />,
          },
          {
            path: 'compositions/oauth-connection-lab',
            element: <StyleGuideCompOAuthConnectionLab />,
          },
          {
            path: 'compositions/post-inspector',
            element: <StyleGuideCompPostInspector />,
          },
          {
            path: 'compositions/unified-media-studio',
            element: <StyleGuideCompUnifiedMediaStudio />,
          },
          {
            path: 'compositions/team-settings',
            element: <StyleGuideCompTeamSettings />,
          },
          {
            path: 'compositions/user-menu',
            element: <StyleGuideCompUserMenu />,
          },
          {
            path: 'compositions/workflow-library',
            element: <StyleGuideCompWorkflowLibrary />,
          },
          {
            path: 'compositions/engagement-inbox',
            element: <StyleGuideCompEngagementInbox />,
          },
          {
            path: 'compositions/business-health-score',
            element: <StyleGuideCompBusinessHealthScore />,
          },
          {
            path: 'compositions/workbench-primitives',
            element: <StyleGuideCompWorkbenchPrimitives />,
          },
          {
            path: 'compositions/tool-view-gallery',
            element: <StyleGuideCompToolViewGallery />,
          },
          {
            path: 'compositions/chat-surfaces',
            element: <StyleGuideCompChatSurfaces />,
          },
          { path: 'voice', element: <StyleGuideVoice /> },
          { path: 'a11y', element: <StyleGuideA11y /> },
        ],
      },
      ...(import.meta.env.DEV ? getDevTestRoutes() : []),
      {
        path: '/admin/tiktok-trends',
        element: (
          <ProtectedRoute>
            <Suspense fallback={<PageLoader />}>
              <TikTokTrendsCuration />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: '/admin',
        element: (
          <ProtectedRoute>
            <Suspense fallback={<PageLoader />}>
              <AdminDashboard />
            </Suspense>
          </ProtectedRoute>
        ),
      },
    ],
    {
      future: {
        v7_startTransition: true,
        v7_relativeSplatPath: true,
        v7_fetcherPersist: true,
        v7_normalizeFormMethod: true,
        v7_partialHydration: true,
        v7_skipActionErrorRevalidation: true,
      },
    },
  );
  return router;
}

let routerInstance: ReturnType<typeof createBrowserRouter> | null = null;

function getRouter() {
  if (!routerInstance) {
    routerInstance = createRouter();
  }
  return routerInstance;
}

const AppRouter = () => {
  return <RouterProvider router={getRouter()} />;
};

export default AppRouter;
